import { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "virsa_theme";

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "dark" || stored === "light") return stored;
      // No explicit choice saved yet — a first-time visitor whose OS/browser
      // is already set to dark mode otherwise always got dropped into light
      // regardless, until they found the toggle themselves. Kept in sync
      // with the same check in public/index.html's inline script, which
      // does this same fallback before React (and this state) even exists.
      return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    } catch {
      return "light";
    }
  });

  // A plain CSS variable swap on <html> is enough to re-theme every page —
  // components style themselves with var(--token) strings, so nothing here
  // needs to re-render when the theme changes.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Live-follows the OS/browser's own dark-mode setting while the app is
  // open (e.g. an OS auto-switching at sunset) — but only for someone who's
  // never made an explicit choice here. Checked fresh against storage on
  // every OS change rather than once at effect-setup time, since this only
  // works correctly now that toggleTheme is the sole place that persists —
  // an explicit choice can happen at any point during the session, and this
  // must stop overriding it the moment it does.
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;

    function onChange(e) {
      let stored;
      try {
        stored = localStorage.getItem(STORAGE_KEY);
      } catch {
        stored = null;
      }
      if (stored === "dark" || stored === "light") return;
      setTheme(e.matches ? "dark" : "light");
    }

    // Safari only got MediaQueryList's addEventListener in version 14
    // (2020) — older versions (still technically within this project's
    // ">0.2%" browserslist target) only have the legacy addListener/
    // removeListener pair instead.
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
    } else if (mq.addListener) {
      mq.addListener(onChange);
    }

    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener("change", onChange);
      } else if (mq.removeListener) {
        mq.removeListener(onChange);
      }
    };
  }, []);

  // Cross-tab sync — the `storage` event fires only on OTHER tabs of the
  // same origin, never the tab that made the write, so this and toggleTheme
  // never race with each other for the tab that actually clicked. Without
  // it, toggling in one tab left every other already-open tab still showing
  // whatever it started with.
  useEffect(() => {
    function onStorage(e) {
      if (e.key !== STORAGE_KEY) return;
      if (e.newValue === "dark" || e.newValue === "light") {
        setTheme(e.newValue);
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Persisting only happens here, not in the effect above — persisting on
  // every change to `theme` would also save the very first render's OS-
  // derived guess as if it were a deliberate choice, the moment this effect
  // first runs. From then on the initializer's `stored === "dark" ||
  // "light"` check would keep reading that locked-in value forever and stop
  // re-checking the OS preference at all, even though the user never
  // actually touched the toggle — so a later OS theme change would silently
  // stop being followed after the very first visit.
  function toggleTheme() {
    setTheme(prev => {
      const next = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Private browsing / storage disabled — the choice just won't persist.
      }
      return next;
    });
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
